import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { MENU_SIDEBAR } from '@/config';
import { useScrollPosition } from '@/hooks/useScrollPosition';
import { useMenus } from '@/providers';
import { useLayout } from '@/providers';
import { deepMerge } from '@/utils';
import { useMenuChildren } from '@/components';
import { MainLayoutConfig } from '.';

// Interface defining the properties of the layout provider context

// Initial layout provider properties, using Main layout configuration as the default
const initalLayoutProps = {
  layout: MainLayoutConfig,
  // Default layout configuration
  headerSticky: false,
  // Header is not sticky by default
  mobileMegaMenuOpen: false,
  // Mobile sidebar is closed by default
  setMobileMegaMenuOpen: open => {
    console.log(`${open}`);
  }
};

// Create a context to manage the layout-related state and logic for Main layout
const MainLayoutContext = createContext(initalLayoutProps);

// Custom hook to access the layout context in other components
const useMainLayout = () => useContext(MainLayoutContext);

// Provider component that sets up the layout state and context for Main layout
const MainLayoutProvider = ({
  children
}) => {
  const {
    pathname
  } = useLocation(); // Gets the current path
  const {
    getLayout,
    setCurrentLayout
  } = useLayout(); // Hook to get and set layout configuration
  const {
    setMenuConfig
  } = useMenus(); // Accesses menu configuration methods
  const secondaryMenu = useMenuChildren(pathname, MENU_SIDEBAR, 0); // Retrieves the secondary menu

  // Sets the primary and secondary menu configurations
  setMenuConfig('primary', MENU_SIDEBAR);
  setMenuConfig('secondary', secondaryMenu);

  // Set the initial state for layout and mobile sidebar.
  // Lazy initializer: the merge result is only ever read once, and this provider
  // re-renders on every scroll frame — no reason to redo the deep merge each time.
  const [layout] = useState(() => deepMerge(MainLayoutConfig, getLayout(MainLayoutConfig.name)));
  const [mobileMegaMenuOpen, setMobileMegaMenuOpen] = useState(false); // Manage state for mobile sidebar

  // Get the current scroll position using a custom hook
  const scrollPosition = useScrollPosition();

  // Calculate whether the header should be sticky based on the scroll position and the layout's sticky offset
  const headerSticky = scrollPosition > layout.options.header.stickyOffset;

  // When the layout state changes, set the current layout configuration in the layout provider
  useEffect(() => {
    setCurrentLayout(layout); // Update the current layout in the global layout state
  }, [layout, setCurrentLayout]); // Re-run this effect if layout or setCurrentLayout changes

  // Memoize the context value so consumers (Header, Navbar, etc.) only re-render when
  // a value they care about actually changes. Without this, scrolling fires setScrollPosition
  // on every scroll event, recreating this object each time and re-rendering the whole header
  // on every pixel of scroll — which, with Google Translate active, makes React and Translate
  // fight over the same DOM nodes and causes the scroll jitter/loop.
  const contextValue = useMemo(() => ({
    layout,
    headerSticky,
    mobileMegaMenuOpen,
    setMobileMegaMenuOpen
  }), [layout, headerSticky, mobileMegaMenuOpen]);

  // Provide the layout state, sticky header state, and sidebar state to children components via context
  return <MainLayoutContext.Provider value={contextValue}>
      {children} {/* Render child components that consume this context */}
    </MainLayoutContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export { MainLayoutProvider, useMainLayout };